<template>
    <div class="modal-container">
        <div class="modal-header">
            <h4 class="modal-title">{{ isUpdate ? 'Update' : 'Create' }} Invoice Adjustment Rule</h4>
            <md-button class="md-simple md-just-icon md-round modal-default-button" @click.stop="$modal.hide">
                <md-icon>clear</md-icon>
            </md-button>
        </div>
        <div class="modal-body">
            <form-wrapper :validator="$v.invoiceAdjustmentRuleDetails" class="form-wrapper">
                <div class="form-container customer-form-container">
                    <div class="contact-details">
                        <form-group attribute="Rule Type" name="invoiceAdjustmentRuleType" label="Rule Type*">
                            <md-input v-model="invoiceAdjustmentRuleDetails.invoiceAdjustmentRuleType" type="hidden" />
                            <vue-select
                                ref="ruleTypeSelect"
                                :reduce="(r) => r"
                                label="type"
                                :options="invoiceAdjustmentRuleTypeOptions"
                                v-model="invoiceAdjustmentRuleDetails.invoiceAdjustmentRuleType"
                                :searchable="$root.isDesktop"
                            ></vue-select>
                        </form-group>
                        <p class="type-message" v-if="invoiceAdjustmentRuleDetails.invoiceAdjustmentRuleType">
                            *{{ invoiceAdjustmentRuleDetails.invoiceAdjustmentRuleType.information }}
                        </p>

                        <form-group
                            v-if="invoiceAdjustmentRuleDetails.invoiceAdjustmentRuleType"
                            attribute="subtype"
                            name="subtype"
                            label="Subtype"
                            required
                        >
                            <md-input v-model="subtype" type="hidden" required />
                            <vue-select
                                :reduce="(r) => r"
                                label="type"
                                :options="invoiceAdjustmentRuleDetails.invoiceAdjustmentRuleType.subtypes"
                                v-model="subtype"
                                :searchable="$root.isDesktop"
                            ></vue-select>
                        </form-group>
                        <div v-if="subtype && subtype.type === 'Every day'">
                            <md-checkbox
                                v-for="(day, index) in daysOfWeek"
                                :key="index"
                                v-model="selectedDays"
                                :value="day.value"
                            >
                                {{ day.label }}
                            </md-checkbox>
                        </div>
                        <p class="type-message" v-if="subtype">*{{ subtype.description }}</p>

                        <form-group attribute="Description" name="description" label="Description*">
                            <md-input v-model.trim="invoiceAdjustmentRuleDetails.description" />
                        </form-group>

                        <div class="md-layout-item md-size-100 enddate-container">
                            <h5 class="ends-title">Ends:</h5>
                            <div class="end-condition-options">
                                <md-radio v-model="endConditionType" value="never">Never</md-radio>
                                <md-radio v-model="endConditionType" value="occurrences">End After</md-radio>
                                <md-radio v-model="endConditionType" value="date">End Date</md-radio>
                            </div>
                            <div v-if="endConditionType === 'occurrences'" class="end-after-occurrences">
                                <form-group label="End After">
                                    <md-input
                                        type="number"
                                        min="1"
                                        v-model.number="invoiceAdjustmentRuleDetails.endAfterOccurrences"
                                    />
                                </form-group>
                            </div>
                            <div v-if="endConditionType === 'date'">
                                <md-datepicker
                                    v-model="invoiceAdjustmentRuleDetails.endDate"
                                    md-immediately
                                    :md-debounce="10"
                                >
                                    <label>End Date</label>
                                </md-datepicker>
                            </div>
                        </div>

                        <div>
                            <div class="amount-switch-container custom-switch-blue">
                                <md-switch
                                    v-model="invoiceAdjustmentRuleDetails.adjustmentIsPercentage"
                                    class="amount-switch"
                                />
                                <span class="amount-switch-container-text">Toggle Percentage</span>
                            </div>
                            <span class="amount-info">
                                Input a negative number for a deduction or a positive number for an addition. (i.e.
                                -3.5)
                            </span>
                            <form-group
                                v-show="!invoiceAdjustmentRuleDetails.adjustmentIsPercentage"
                                attribute="amount"
                                name="unitPrice"
                                label="Amount"
                            >
                                <span v-if="invoiceAdjustmentRuleDetails.unitPrice" class="currency-container">
                                    {{ null | currency(user.teamCurrency, true) }}
                                </span>
                                <md-input type="number" v-model="invoiceAdjustmentRuleDetails.unitPrice" required />
                            </form-group>

                            <form-group
                                v-show="invoiceAdjustmentRuleDetails.adjustmentIsPercentage"
                                name="percentageAmount"
                                label="Percentage"
                                attribute="Percentage"
                            >
                                <md-input
                                    type="number"
                                    v-model="invoiceAdjustmentRuleDetails.percentageAmount"
                                    required
                                />
                                <md-tooltip md-direction="top">
                                    {{ $t('invoicing.adjustments.percentageTooltip') }}
                                </md-tooltip>
                            </form-group>
                        </div>
                        <account-code-description-group
                            :account-code="invoiceAdjustmentRuleDetails.accountCode"
                            :account-code-description="invoiceAdjustmentRuleDetails.accountCodeDescription"
                            :is-update="isUpdate"
                            @optionSelected="handleAccountCodeChanged"
                            @optionDescriptionSelected="handleAccountDescriptionChanged"
                        />

                        <form-group name="tags" label="Tags">
                            <md-input v-model="invoiceAdjustmentRuleDetails.tags" type="hidden" />
                            <vue-select
                                data-testid="adjustment-rule-tag"
                                :reduce="(r) => r"
                                label="name"
                                :options="tags"
                                v-model="invoiceAdjustmentRuleDetails.tags"
                                :searchable="$root.isDesktop"
                                multiple
                                class="tag-background"
                                append-to-body
                            ></vue-select>
                        </form-group>
                    </div>
                </div>
            </form-wrapper>
        </div>
        <div class="modal-footer">
            <md-button class="dialog-button md-primary" @click="validateRule">
                {{ isUpdate ? 'Update' : 'Create' }}
            </md-button>
            <md-button class="dialog-button md-default" @click.stop="$modal.hide">Cancel</md-button>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { handleRequests, showErrorMessage } from '@/helpers';
import { required, maxLength, requiredIf } from 'vuelidate/lib/validators';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { INVOICE_ADJUSTMENT_RULE_TYPES, RATES_DAYS_OF_THE_WEEK, DATE_TYPES } from '@/utils/constants';
import AccountCodeDescriptionGroup from '../../../components/AccountCodeDescriptionGroup';

export default {
    name: 'CreateInvoiceAdjustmentRuleModal',
    components: {
        AccountCodeDescriptionGroup
    },
    mixins: [GeneralMixin],
    computed: {
        ...mapGetters({
            user: 'user/user'
        })
    },
    data() {
        return {
            invoiceAdjustmentRuleTypeOptions: [...INVOICE_ADJUSTMENT_RULE_TYPES],
            typeDescription: '',
            loadingFinished: false,
            tags: [],
            subtype: null,
            daysOfWeek: [...RATES_DAYS_OF_THE_WEEK],
            selectedDays: [],
            adjustmentIsPercentage: false,
            endConditionType: 'never',
        };
    },
    props: {
        invoiceAdjustmentRuleDetails: {
            type: Object,
            default: () => {}
        },
        isUpdate: {
            type: Boolean,
            default: false
        },
        resolve: {
            type: Function,
            default: () => {}
        }
    },
    async mounted() {
        if (this.invoiceAdjustmentRuleDetails && this.invoiceAdjustmentRuleDetails.endAfterOccurrences) {
            this.endConditionType = 'occurrences';
        } else if (this.invoiceAdjustmentRuleDetails && this.invoiceAdjustmentRuleDetails.endDate) {
            this.endConditionType = 'date';
        } else {
            this.endConditionType = 'never';
        }
        if (this.invoiceAdjustmentRuleDetails && this.invoiceAdjustmentRuleDetails.invoiceAdjustmentRuleType) {
            const invoiceType = this.findInvoiceType(this.invoiceAdjustmentRuleDetails.invoiceAdjustmentRuleType);
            this.invoiceAdjustmentRuleDetails.invoiceAdjustmentRuleType = invoiceType;
            this.invoiceAdjustmentRuleDetails.invoiceAdjustmentRuleTypeId = invoiceType?.value;
            this.subtype = this.getScheduleType(
                invoiceType.subtypes,
                this.invoiceAdjustmentRuleDetails.adjustmentScheduleTypeId
            );
            this.selectedDays = this.getDaysFromFlag(this.invoiceAdjustmentRuleDetails.applyToDaysOfTheWeek);

            this.setInvoiceAdjustmentTypeDescription(this.invoiceAdjustmentRuleDetails.invoiceAdjustmentRuleTypeId);

            if (this.invoiceAdjustmentRuleDetails.percentageAmount) {
                this.invoiceAdjustmentRuleDetails.adjustmentIsPercentage = true;
            } else {
                this.invoiceAdjustmentRuleDetails.adjustmentIsPercentage = false;
            }

            this.invoiceAdjustmentRuleDetails.endDate = this.invoiceAdjustmentRuleDetails.endDate
                ? moment(this.invoiceAdjustmentRuleDetails.endDate).format(DATE_TYPES.internationalDate)
                : null;
        } else {
            this.loadingFinished = true;
        }

        await this.getTags();
        this.invoiceAdjustmentRuleDetails.tagsDeleted = [];
        this.invoiceAdjustmentRuleDetails.tagsAdded = [];
    },
    watch: {
        'invoiceAdjustmentRuleDetails.invoiceAdjustmentRuleType': {
            immediate: false,
            handler(newValue) {
                if (this.$refs.ruleTypeSelect && (!this.isUpdate || this.loadingFinished)) {
                    if (this.invoiceAdjustmentRuleDetails.invoiceAdjustmentRuleType.type !== newValue.type) {
                        this.typeDescription = newValue ? newValue.information : '';
                        this.subtype = null;
                    }                    
                }
            }
        },
        'invoiceAdjustmentRuleDetails.tags': function(newValue, oldValue) {
            const added = newValue.filter((tag) => !tag.invoiceAdjustmentTagId);
            const deleted = oldValue.filter(
                (tag) =>
                    !newValue.includes(tag) &&
                    tag.invoiceAdjustmentTagId &&
                    !oldValue.includes((x) => x.invoiceAdjustmentTagId) !== tag.invoiceAdjustmentTagId
            );

            this.invoiceAdjustmentRuleDetails.tagsAdded = [...added];
            deleted.forEach((tag) => this.invoiceAdjustmentRuleDetails.tagsDeleted.push(tag));
        },
        'invoiceAdjustmentRuleDetails.adjustmentIsPercentage': function(newVal, oldVal) {
            this.invoiceAdjustmentRuleDetails.unitPrice = null;
            this.invoiceAdjustmentRuleDetails.percentageAmount = null;
        },
        endConditionType(newVal) {
            this.handleEndConditionChange();
        }
    },
    methods: {
        handleEndConditionChange() {
            if (this.endConditionType === 'never') {
                this.invoiceAdjustmentRuleDetails.endAfterOccurrences = null;
                this.invoiceAdjustmentRuleDetails.endDate = null;
            } else if (this.endConditionType === 'occurrences') {
                this.invoiceAdjustmentRuleDetails.endDate = null;
            } else if (this.endConditionType === 'date') {
                this.invoiceAdjustmentRuleDetails.endAfterOccurrences = null;
            }
        },
        async create() {
            this.$_handleLoaderState(true, 'SAVING...');

            const api = `/api/invoice-adjustment-rules`;
            const payload = {
                method: 'post',
                data: this.getMappedData()
            };

            try {
                const response = await handleRequests(api, payload);
                const invoiceAdjustmentRuleId = response.data;
                if (invoiceAdjustmentRuleId && invoiceAdjustmentRuleId > 0) {
                    this.$notifySuccess('Successfully created invoice adjustment rule.');
                    this.$v.$reset();
                    this.resolve('ok');
                }
            } catch (error) {
                const message = 'Error in creating the invoice adjustment rule';
                showErrorMessage(this, message, error);
            }
            this.$_handleLoaderState(false);
        },
        async update() {
            this.$_handleLoaderState(true, 'UPDATING...');
            const api = `/api/invoice-adjustment-rules/${this.invoiceAdjustmentRuleDetails.invoiceAdjustmentRuleId}`;
            const payload = {
                method: 'put',
                data: this.getMappedData()
            };

            try {
                await handleRequests(api, payload);
                this.$notifySuccess('Successfully updated invoice adjustment rule.');
                this.$v.$reset();
                this.resolve('ok');
            } catch (error) {
                const message = 'Error in updating the invoice adjustment rule';
                showErrorMessage(this, message, error);
            }
            this.$_handleLoaderState(false);
        },
        getMappedData() {
            return {
                invoiceAdjustmentRuleType:
                    this.invoiceAdjustmentRuleDetails.invoiceAdjustmentRuleType?.value ??
                    this.invoiceAdjustmentRuleDetails.invoiceAdjustmentRuleTypeId,
                taxSettingsId: this.invoiceAdjustmentRuleDetails.taxSettingsId,
                description: this.invoiceAdjustmentRuleDetails.description,
                unitPrice: this.invoiceAdjustmentRuleDetails.unitPrice,
                accountCode: this.invoiceAdjustmentRuleDetails.accountCode,
                tagsAdded: this.invoiceAdjustmentRuleDetails.tagsAdded,
                tagsDeleted: this.invoiceAdjustmentRuleDetails.tagsDeleted,
                adjustmentScheduleTypeId: this.subtype.value,
                applyToDaysOfTheWeek: this.selectedDays?.reduce((day, val) => day | val, 0),
                percentageAmount: this.invoiceAdjustmentRuleDetails.percentageAmount,
                endDate: this.invoiceAdjustmentRuleDetails.endDate
                    ? moment(this.invoiceAdjustmentRuleDetails.endDate).format(DATE_TYPES.internationalDate)
                    : null,
                endAfterOccurrences: this.invoiceAdjustmentRuleDetails.endAfterOccurrences
            };
        },
        handleAccountCodeChanged(value) {
            this.invoiceAdjustmentRuleDetails.accountCode = value;
        },
        handleAccountDescriptionChanged(value) {
            this.invoiceAdjustmentRuleDetails.accountCodeDescription = value;
        },
        validateRule() {
            this.$v.subtype.$touch();

            if (this.$v.subtype.$invalid) {
                this.$notifyError('Subtype is required');
                return;
            }

            this.$v.invoiceAdjustmentRuleDetails.$touch();
            if (!this.$v.invoiceAdjustmentRuleDetails.$invalid) {
                if (this.isUpdate) {
                    this.update();
                } else {
                    this.create();
                }
            }
        },
        setInvoiceAdjustmentTypeDescription(val) {
            const typeSelected = this.findInvoiceType(val);
            this.typeDescription = typeSelected ? typeSelected.information : '';
            setTimeout(() => {
                this.loadingFinished = true;
            }, 1000);
        },
        findInvoiceType(value) {
            return this.invoiceAdjustmentRuleTypeOptions.find((x) => x.value === value);
        },
        async getTags() {
            const api = `/api/teams/settings/tags?showDeletedTags=false`;
            try {
                const response = await handleRequests(api);
                if (response?.data)
                    this.tags = [...response.data];
            } catch (error) {
                const message = 'Error in getting tags';
                showErrorMessage(this, message, null);
            }
        },
        getScheduleType(adjustmentTypes, adjustmentScheduleTypeId) {
            if (!adjustmentScheduleTypeId)
                return null;

            return adjustmentTypes.find((x) => x.value === adjustmentScheduleTypeId);
        },
        getDaysFromFlag(bitFlag) {
            if (!bitFlag) {
                return [];
            }

            return this.daysOfWeek.filter((day) => (bitFlag & day.value) !== 0).map((day) => day.value);
        }
    },
    validations: {
        invoiceAdjustmentRuleDetails: {
            description: { required, maxLength: maxLength(100) },
            unitPrice: {
                required:
                    // eslint-disable-next-line func-names
                    requiredIf(function() {
                        return !this.invoiceAdjustmentRuleDetails.adjustmentIsPercentage;
                    })
            },
            percentageAmount: {
                required:
                    // eslint-disable-next-line func-names
                    requiredIf(function() {
                        return this.invoiceAdjustmentRuleDetails.adjustmentIsPercentage;
                    })
            },
            accountCode: {},
            taxSettingsId: {},
            invoiceAdjustmentRuleType: { required },
            endDate: {}
        },
        subtype: { required }
    }
};
</script>

<style lang="scss" scoped>
::v-deep .md-menu-content.md-select-menu {
    width: 150px;
}
.modal-container {
    max-width: 600px;
}

.popup {
    position: absolute;
    top: 0;
    left: 100%;
}

.type-message {
    font-size: smaller;
}

::v-deep .tag-background .vs__selected {
    background-color: #42b983 !important;
    color: white;
}

.amount-switch-container {
    display: flex;
}

.currency-container {
    padding-top: 7px;
    margin-right: 4px;
    cursor: text !important;
}

.enddate-container {
    padding: 0 !important;
    .div .button {
        background-color: transparent !important;
    }
}

.amount-switch-container-text {
    align-content: center;
    font-size: 12px;
}

.amount-info {
    font-size: 12px;
}

.ends-title {
    font-weight: 400;
    margin: 0px;
    padding-top: 5px;
}
</style>
